import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axiosConfig";

const CATALOG_CACHE_NS = "xphub_catalog_cache_v2";
const CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 6; // 6 horas

function pickList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.games)) return data.games;
  if (Array.isArray(data.data)) return data.data;
  const arrays = Object.values(data).filter(Array.isArray);
  return arrays.length ? arrays.sort((a, b) => (b?.length || 0) - (a?.length || 0))[0] : [];
}

function adaptSteamList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((g, idx) => {
      const id =
        g.id ??
        g.appid ??
        g._id ??
        (typeof g.app === "object" ? g.app?.id : undefined) ??
        idx;

      const name =
        g.name ??
        g.title ??
        g.appName ??
        (typeof g.app === "object" ? g.app?.name : undefined) ??
        "Juego de Steam";

      const img =
        g.background_image ||
        g.image ||
        g.header_image ||
        g.capsule_image ||
        g.img ||
        null;

      return {
        ...g,
        id,
        name,
        background_image: img,
        players:
          typeof g.players === "string"
            ? Number(g.players.replace(/[^\d]/g, "")) || undefined
            : g.players,
        parent_platforms: Array.isArray(g.parent_platforms) ? g.parent_platforms : [],
      };
    })
    .filter((g) => g.name && g.background_image);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function getWithRetry(url, { attempts = 2, retryDelayMs = 360 } = {}) {
  let lastError = null;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await api.get(url);
    } catch (error) {
      lastError = error;
      if (attempt < attempts) {
        await sleep(retryDelayMs * attempt);
      }
    }
  }
  throw lastError || new Error(`Request failed: ${url}`);
}

function readCatalogCache(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const savedAt = Number(parsed.savedAt || 0);
    if (!Number.isFinite(savedAt) || savedAt <= 0) return null;
    if (Date.now() - savedAt > CACHE_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCatalogCache(cacheKey, payload) {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch {
    // noop
  }
}

export function useGameData(selectedPlatform = "") {
  const [topRatedGames, setTopRated] = useState([]);
  const [latestGames, setLatest] = useState([]);
  const [bestOfYear, setBestOfYear] = useState([]);
  const [steamGames, setSteam] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [loading, setLoading] = useState({
    topRated: true,
    latest: true,
    bestOfYear: true,
    steam: true,
  });
  const [errors, setErrors] = useState({
    catalog: "",
    steam: "",
  });

  const platformQS = useMemo(
    () => (selectedPlatform ? `?platform=${encodeURIComponent(selectedPlatform)}` : ""),
    [selectedPlatform]
  );

  const platformCacheKey = useMemo(() => {
    const suffix = selectedPlatform ? String(selectedPlatform).toLowerCase().trim() : "all";
    return `${CATALOG_CACHE_NS}:${suffix}`;
  }, [selectedPlatform]);

  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => {
    setReloadToken((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let alive = true;

    async function fetchAll() {
      const cached = readCatalogCache(platformCacheKey);
      const cachedBest = Array.isArray(cached?.bestOfYear) ? cached.bestOfYear : [];
      const cachedTop = Array.isArray(cached?.topRatedGames) ? cached.topRatedGames : [];
      const cachedLatest = Array.isArray(cached?.latestGames) ? cached.latestGames : [];
      const cachedSteam = Array.isArray(cached?.steamGames) ? cached.steamGames : [];

      if (cached) {
        if (!alive) return;
        setBestOfYear(cachedBest);
        setTopRated(cachedTop);
        setLatest(cachedLatest);
        setSteam(cachedSteam);
        setLastUpdatedAt(Number(cached.savedAt));
        setLoading({ topRated: false, latest: false, bestOfYear: false, steam: false });
      } else {
        if (!alive) return;
        setBestOfYear([]);
        setTopRated([]);
        setLatest([]);
        setSteam([]);
        setLastUpdatedAt(null);
        setLoading({ topRated: true, latest: true, bestOfYear: true, steam: true });
      }

      setErrors({ catalog: "", steam: "" });

      const [bestRes, topRes, latestRes] = await Promise.allSettled([
        getWithRetry(`/games/best-of-year${platformQS}`),
        getWithRetry(`/games/top-rated${platformQS}`),
        getWithRetry(`/games/latest${platformQS}`),
      ]);

      if (!alive) return;

      let nextBest = cachedBest;
      let nextTop = cachedTop;
      let nextLatest = cachedLatest;
      let catalogFailed = 0;

      if (bestRes.status === "fulfilled") {
        nextBest = pickList(bestRes.value?.data);
      } else {
        catalogFailed += 1;
      }

      if (topRes.status === "fulfilled") {
        nextTop = pickList(topRes.value?.data);
      } else {
        catalogFailed += 1;
      }

      if (latestRes.status === "fulfilled") {
        nextLatest = pickList(latestRes.value?.data);
      } else {
        catalogFailed += 1;
      }

      setBestOfYear(nextBest);
      setTopRated(nextTop);
      setLatest(nextLatest);
      setLoading((s) => ({ ...s, bestOfYear: false, topRated: false, latest: false }));

      if (catalogFailed >= 3) {
        setErrors((s) => ({
          ...s,
          catalog: "No se pudo cargar el catálogo ahora mismo. Se muestran los últimos datos guardados.",
        }));
      } else if (catalogFailed > 0) {
        setErrors((s) => ({
          ...s,
          catalog: "Parte del catálogo no se pudo actualizar. Seguimos mostrando los datos disponibles.",
        }));
      }

      let nextSteam = cachedSteam;
      let steamLoaded = false;
      try {
        let steamData = [];
        try {
          const s1 = await getWithRetry(`/games/steam-trending`);
          steamData = pickList(s1.data);
        } catch {
          const s2 = await getWithRetry(`/games/steam-top`);
          steamData = pickList(s2.data);
        }

        nextSteam = adaptSteamList(steamData);
        steamLoaded = true;
      } catch (e) {
        if (!alive) return;
        console.error("Steam feed error:", e?.message || e);
        setErrors((s) => ({
          ...s,
          steam: "No se pudo cargar tendencias de Steam temporalmente.",
        }));
      }

      if (!alive) return;
      setSteam(nextSteam);
      setLoading((s) => ({ ...s, steam: false }));

      const hadFreshCatalog = catalogFailed < 3;
      if (hadFreshCatalog || steamLoaded) {
        const now = Date.now();
        setLastUpdatedAt(now);
        writeCatalogCache(platformCacheKey, {
          savedAt: now,
          bestOfYear: nextBest,
          topRatedGames: nextTop,
          latestGames: nextLatest,
          steamGames: nextSteam,
        });
      }
    }

    fetchAll();
    return () => {
      alive = false;
    };
  }, [platformQS, platformCacheKey, reloadToken]);

  return {
    topRatedGames,
    latestGames,
    steamGames,
    bestOfYear,
    loading,
    errors,
    refresh,
    lastUpdatedAt,
  };
}
